import { callctl, type ApiClient, type MonitorMode } from "@savvy/runtime";
import { create } from "zustand";

/**
 * The supervisor's monitor session — at most ONE at a time.
 *
 * This store exists to fix a bug found live during the Phase 3 gate
 * (BACKLOG §3): the PoC console threw away the supervisor_uuid that
 * POST /monitor returns, so pressing Barge while already whispering
 * re-originated a SECOND call to a supervisor extension still busy in the
 * first — which rang unanswered forever. Keeping the uuid is what makes
 * "switch mode" and "stop" possible at all.
 *
 * MODE SWITCHING IS STOP-THEN-START, deliberately. The server does have
 * PUT /monitor/{uuid}/mode, but it rides eavesdrop's DTMF handler, and those
 * digits may TOGGLE the mux flags rather than set them — the MonitorVars
 * mapping was already proven backwards once on a live call, so nothing rides
 * on unverified digit semantics. Flip VITE_MONITOR_MODE_SWITCH=in-place once
 * someone verifies the digits the same way whisper's direction was verified.
 */

const SWITCH_MODE: "restart" | "in-place" =
  import.meta.env?.VITE_MONITOR_MODE_SWITCH === "in-place" ? "in-place" : "restart";

export interface MonitorSession {
  targetUuid: string;
  supervisorUuid: string;
  mode: MonitorMode;
}

export interface MonitorState {
  session: MonitorSession | null;
  pending: boolean;
  error: string | null;

  monitor(api: ApiClient, targetUuid: string, supervisorExt: string, mode: MonitorMode): Promise<void>;
  stop(api: ApiClient): Promise<void>;
  /** Wired to the realtime stream: an ended call clears any session it was part of. */
  onCallEnded(uuid: string): void;
}

export const useMonitor = create<MonitorState>((set, get) => ({
  session: null,
  pending: false,
  error: null,

  async monitor(api, targetUuid, supervisorExt, mode) {
    const { session, pending } = get();
    if (pending) return;
    if (session && session.targetUuid === targetUuid && session.mode === mode) return; // no-op

    set({ pending: true, error: null });
    const ctl = callctl(api);
    try {
      // Never a second bare POST while a leg is live: the supervisor's phone is
      // busy in the current session, so a fresh originate can only ring out.
      if (session) {
        if (SWITCH_MODE === "in-place" && session.targetUuid === targetUuid) {
          await ctl.setMonitorMode(targetUuid, mode);
          set({ session: { ...session, mode }, pending: false });
          return;
        }
        await ctl.hangup(session.supervisorUuid);
        set({ session: null });
      }
      const res = await ctl.monitorStart(targetUuid, supervisorExt, mode);
      set({
        session: { targetUuid, supervisorUuid: res.supervisor_uuid, mode: res.mode },
        pending: false,
      });
    } catch (e) {
      set({ pending: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  async stop(api) {
    const { session } = get();
    if (!session) return;
    set({ pending: true, error: null });
    try {
      await callctl(api).hangup(session.supervisorUuid);
      set({ session: null, pending: false });
    } catch (e) {
      // The leg may already be dead (the call ended); clearing is still right.
      set({ session: null, pending: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  onCallEnded(uuid) {
    const { session } = get();
    if (session && (session.supervisorUuid === uuid || session.targetUuid === uuid)) {
      // The old console had no way to know its monitor leg died — that is how
      // it accumulated the stale state behind the original bug.
      set({ session: null });
    }
  },
}));
